import React, { useEffect, useState, useMemo } from 'react';
import { departmentApi } from '@/features/workspace/api';
import {
  SearchGlassIcon,
  DownloadFileIcon,
  ChevronRightArrowIcon,
} from './DepartmentIcons';
import { DoctoralDossierModal } from './DoctoralDossierModal';
import { useToast } from '@/hooks/useToast';

export const DepartmentMonitoringView: React.FC = () => {
  const { notify } = useToast();
  const [data, setData] = useState<any>(null);
  const [, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [course, setCourse] = useState<string>('all');
  const [supervisor, setSupervisor] = useState<string>('all');
  const [program, setProgram] = useState<string>('all');
  const [defenseStatus, setDefenseStatus] = useState<string>('all');
  const [nirdStatus, setNirdStatus] = useState<string>('all');
  const [quickFilter, setQuickFilter] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'years' | 'supervisors' | 'registry'>('overview');

  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [showEnrollModal, setShowEnrollModal] = useState<boolean>(false);
  const [isEnrolling, setIsEnrolling] = useState<boolean>(false);
  const [enrollForm, setEnrollForm] = useState({
    full_name: '',
    student_code: '',
    course: 1,
    admission_year: 2025,
    program_code: '8D01717',
    program_title: 'Казахский язык и литература',
    supervisor_name: 'Syrym Zhanzhigitov',
    foreign_consultant_name: '',
    workplace: 'ЕНУ им. Л.Н. Гумилева',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (search.trim()) params.search = search.trim();
      if (course === 'graduates') params.academic_status = 'GRADUATE';
      else if (course === 'archive') params.academic_status = 'ALUMNI_ARCHIVED';
      else if (course === 'studying') params.academic_status = 'STUDYING';
      else if (course !== 'all') params.course = Number(course);
      if (supervisor !== 'all') params.supervisor = supervisor;
      if (program !== 'all') params.program = program;
      if (defenseStatus !== 'all') params.defense_status = defenseStatus;
      if (nirdStatus !== 'all') params.nird = nirdStatus;
      if (quickFilter) params.quick_filter = quickFilter;

      const res = await departmentApi.monitoring(params);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [course, supervisor, program, defenseStatus, nirdStatus, quickFilter]);

  const toggleQuickFilter = (key: string) => {
    setQuickFilter((prev) => (prev === key ? null : key));
  };

  const applyKpiFilter = (action: 'all' | 'defended' | 'without_defense' | 'studying' | 'nird_risk' | 'supervisors' | 'graduates' | 'diploma' | 'employed') => {
    if (action === 'all') {
      handleResetFilters();
      return;
    }
    if (action === 'supervisors') {
      setActiveTab('supervisors');
      return;
    }
    setDefenseStatus('all');
    setNirdStatus('all');
    setCourse('all');
    if (action === 'defended') {
      setQuickFilter('defended');
      setActiveTab('registry');
      return;
    }
    if (action === 'without_defense') {
      setQuickFilter('without_defense');
      setActiveTab('registry');
      return;
    }
    if (action === 'studying') {
      setQuickFilter(null);
      setCourse('studying');
      setActiveTab('registry');
      return;
    }
    if (action === 'nird_risk') {
      setQuickFilter('nird_unsubmitted');
      setActiveTab('registry');
      return;
    }
    if (action === 'graduates') {
      setQuickFilter('graduates');
      setActiveTab('registry');
      return;
    }
    if (action === 'diploma') {
      setQuickFilter('diploma_verified');
      setActiveTab('registry');
      return;
    }
    if (action === 'employed') {
      setQuickFilter('working');
      setActiveTab('registry');
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchData();
  };

  const handleResetFilters = () => {
    setSearch('');
    setCourse('all');
    setSupervisor('all');
    setProgram('all');
    setDefenseStatus('all');
    setNirdStatus('all');
    setQuickFilter(null);
  };

  const handleExportCsv = () => {
    if (!data?.students?.length) return;
    const headers = [
      '#',
      'ФИО',
      'Шифр',
      'Возраст',
      'Курс',
      'Программа',
      'Место работы',
      'НИРД',
      'Прогресс %',
      'Научный руководитель',
      'Статус защиты',
      'Дата защиты',
    ];
    const rows = data.students.map((s: any, idx: number) => [
      idx + 1,
      `"${s.full_name}"`,
      `"${s.student_code}"`,
      s.age,
      s.course,
      `"${s.program_code} · ${s.program_title}"`,
      `"${s.workplace}"`,
      `"${s.nird_status}"`,
      s.progress,
      `"${s.supervisor_name}"`,
      `"${s.defense_status}"`,
      `"${s.defense_date || ''}"`,
    ]);

    const csvContent = [headers.join(','), ...rows.map((r: any) => r.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `monitoring_doctoral_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEnrollSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enrollForm.full_name.trim()) return;
    try {
      setIsEnrolling(true);
      await departmentApi.enrollDoctoralStudent({
        ...enrollForm,
        student_code: enrollForm.student_code.trim() || `D017-${Math.floor(100 + Math.random() * 900)}`,
      });
      setShowEnrollModal(false);
      setEnrollForm({
        full_name: '',
        student_code: '',
        course: 1,
        admission_year: 2025,
        program_code: '8D01717',
        program_title: 'Казахский язык и литература',
        supervisor_name: 'Syrym Zhanzhigitov',
        foreign_consultant_name: '',
        workplace: 'ЕНУ им. Л.Н. Гумилева',
      });
      await fetchData();
    } catch {
      notify('Ошибка при зачислении докторанта', 'error');
    } finally {
      setIsEnrolling(false);
    }
  };

  const kpis = data?.kpis || {
    doctoral_count: 12,
    in_selection: 12,
    nird_risk: 2,
    avg_progress: 74,
    defended_count: 3,
    without_defense_date_count: 9,
    studying_count: 12,
    supervisors_count: 3,
  };

  const students = data?.students || [];
  const defendedList = data?.defended || [];
  const withoutDefenseList = data?.without_defense || [];
  const riskStudents = data?.risk_students || [];
  const cohortStats = data?.cohort_stats || [];
  const supervisorStats = data?.supervisor_stats || [];

  const groupedByYear = useMemo(() => {
    const map: Record<number, any[]> = { 1: [], 2: [], 3: [] };
    students.forEach((s: any) => {
      if (!map[s.course]) map[s.course] = [];
      map[s.course].push(s);
    });
    return map;
  }, [students]);

  const groupedBySupervisor = useMemo(() => {
    const map: Record<string, any[]> = {};
    students.forEach((s: any) => {
      if (!map[s.supervisor_name]) map[s.supervisor_name] = [];
      map[s.supervisor_name].push(s);
    });
    return map;
  }, [students]);

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-6">
        <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
          Academia · контроль докторантуры
        </span>
        <h2 className="text-2xl font-bold text-white tracking-tight mt-1">
          Мониторинг докторантов
        </h2>
        <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-4xl">
          Единый центр мониторинга докторантуры кафедры: объединяет контингент, темы, ИУП, НИРД, руководителей, занятость, архив и контроль защит.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <button
            type="button"
            onClick={() => applyKpiFilter('all')}
            className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80 text-left hover:border-cyan-400/50 transition-colors cursor-pointer"
          >
            <span className="text-[11px] font-semibold uppercase text-slate-400">В реестре</span>
            <div className="text-3xl font-extrabold text-white mt-1">{kpis.doctoral_count}</div>
            <span className="text-xs text-cyan-400 mt-1 block">единый источник · сброс фильтров</span>
          </button>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80 text-left">
            <span className="text-[11px] font-semibold uppercase text-slate-400">В выборке</span>
            <div className="text-3xl font-extrabold text-white mt-1">{kpis.in_selection}</div>
            <span className="text-xs text-cyan-400 mt-1 block">по текущим фильтрам</span>
          </div>
          <button
            type="button"
            onClick={() => applyKpiFilter('nird_risk')}
            className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80 text-left hover:border-rose-400/50 transition-colors cursor-pointer"
          >
            <span className="text-[11px] font-semibold uppercase text-slate-400">НИРД-риск</span>
            <div className="text-3xl font-extrabold text-rose-400 mt-1">{kpis.nird_risk}</div>
            <span className="text-xs text-rose-300 mt-1 block">требуют внимания · фильтр</span>
          </button>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80 text-left">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Средний прогресс</span>
            <div className="text-3xl font-extrabold text-white mt-1">{kpis.avg_progress}%</div>
            <span className="text-xs text-cyan-400 mt-1 block">по досье</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="p-3.5 rounded-xl border border-cyan-500/20 bg-[#091b29] text-left">
          <span className="text-[10px] font-semibold uppercase text-slate-400 block">В выборке</span>
          <div className="text-2xl font-black text-white mt-0.5">{kpis.in_selection}</div>
          <span className="text-[11px] text-cyan-400">докторантов</span>
        </div>
        <button
          type="button"
          onClick={() => applyKpiFilter('defended')}
          className="p-3.5 rounded-xl border border-emerald-500/20 bg-[#091b29] text-left hover:border-emerald-400/60 transition-colors cursor-pointer"
        >
          <span className="text-[10px] font-semibold uppercase text-slate-400 block">Защищено</span>
          <div className="text-2xl font-black text-emerald-400 mt-0.5">{kpis.defended_count}</div>
          <span className="text-[11px] text-emerald-400">есть дата защиты · фильтр</span>
        </button>
        <button
          type="button"
          onClick={() => applyKpiFilter('without_defense')}
          className="p-3.5 rounded-xl border border-rose-500/20 bg-[#091b29] text-left hover:border-rose-400/60 transition-colors cursor-pointer"
        >
          <span className="text-[10px] font-semibold uppercase text-slate-400 block">Без даты</span>
          <div className="text-2xl font-black text-rose-400 mt-0.5">{kpis.without_defense_date_count}</div>
          <span className="text-[11px] text-rose-300">нужен контроль статуса · фильтр</span>
        </button>
        <button
          type="button"
          onClick={() => applyKpiFilter('studying')}
          className="p-3.5 rounded-xl border border-cyan-500/20 bg-[#091b29] text-left hover:border-cyan-400/60 transition-colors cursor-pointer"
        >
          <span className="text-[10px] font-semibold uppercase text-slate-400 block">Обучаются</span>
          <div className="text-2xl font-black text-white mt-0.5">{kpis.studying_count}</div>
          <span className="text-[11px] text-cyan-400">1–3 курс · фильтр</span>
        </button>
        <button
          type="button"
          onClick={() => applyKpiFilter('supervisors')}
          className="p-3.5 rounded-xl border border-amber-500/20 bg-[#091b29] text-left col-span-2 sm:col-span-1 hover:border-amber-400/60 transition-colors cursor-pointer"
        >
          <span className="text-[10px] font-semibold uppercase text-slate-400 block">Руководителей</span>
          <div className="text-2xl font-black text-amber-400 mt-0.5">{kpis.supervisors_count}</div>
          <span className="text-[11px] text-amber-400">в выборке · вкладка</span>
        </button>
      </div>

      <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5 space-y-4">
        <form onSubmit={handleSearchSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2 relative">
            <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Поиск</span>
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ФИО, ID, место работы, руководитель"
                className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 pl-9 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
              />
              <div className="absolute left-3 top-2.5 text-slate-400">
                <SearchGlassIcon className="w-3.5 h-3.5" />
              </div>
            </div>
          </div>

          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Курс</span>
            <select
              value={course}
              onChange={(e) => setCourse(e.target.value)}
              className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
            >
              <option value="all">Все</option>
              <option value="1">1 курс</option>
              <option value="2">2 курс</option>
              <option value="3">3 курс</option>
              <option value="studying">Обучаются (1–3 курс)</option>
              <option value="graduates">Выпускники</option>
              <option value="archive">Архив</option>
            </select>
          </div>

          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Научный руководитель</span>
            <select
              value={supervisor}
              onChange={(e) => setSupervisor(e.target.value)}
              className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
            >
              <option value="all">Все руководители</option>
              <option value="Syrym Zhanzhigitov">Syrym Zhanzhigitov</option>
              <option value="А. Қайдарова">А. Қайдарова</option>
              <option value="Б. Омарова">Б. Омарова</option>
              <option value="Жусупова А.К.">Жусупова А.К.</option>
            </select>
          </div>

          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Программа</span>
            <select
              value={program}
              onChange={(e) => setProgram(e.target.value)}
              className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
            >
              <option value="all">Все программы</option>
              <option value="8D01717">8D01717</option>
              <option value="8D02305">8D02305</option>
              <option value="8D02301">8D02301</option>
            </select>
          </div>

          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Статус защиты</span>
            <select
              value={defenseStatus}
              onChange={(e) => setDefenseStatus(e.target.value)}
              className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
            >
              <option value="all">Все</option>
              <option value="Защищено">Защищено</option>
              <option value="Без даты защиты">Без даты защиты</option>
            </select>
          </div>
        </form>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-800">
          <div className="flex flex-wrap items-center gap-2">
            {[
              { label: '1 курс', key: 'course_1' },
              { label: '2 курс', key: 'course_2' },
              { label: '3 курс', key: 'course_3' },
              { label: 'Защищённые', key: 'defended' },
              { label: 'Без даты защиты', key: 'without_defense' },
              { label: 'Выпускники', key: 'graduates' },
              { label: 'Диплом верифицирован', key: 'diploma_verified' },
              { label: 'Трудоустроены', key: 'working' },
              { label: 'До 30 лет', key: 'under_30' },
              { label: 'Не сдали НИРД', key: 'nird_unsubmitted' },
            ].map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => toggleQuickFilter(chip.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  quickFilter === chip.key
                    ? 'bg-cyan-500 text-slate-950 shadow-md font-bold'
                    : 'bg-[#061420] text-slate-300 hover:text-white border border-slate-700 hover:border-cyan-500/50'
                }`}
              >
                {chip.label}
              </button>
            ))}
            <button
              type="button"
              onClick={handleResetFilters}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              Сбросить
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowEnrollModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs transition-all shadow active:scale-95"
            >
              <span>+ Зачислить докторанта</span>
            </button>

            <button
              type="button"
              onClick={handleExportCsv}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold text-xs transition-all shadow active:scale-95"
            >
              <DownloadFileIcon className="w-3.5 h-3.5" />
              <span>Экспорт CSV</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-800 pb-2">
        {[
          { id: 'overview', label: 'Обзор' },
          { id: 'years', label: 'По годам' },
          { id: 'supervisors', label: 'По руководителям' },
          { id: 'registry', label: 'Полный реестр' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              activeTab === tab.id
                ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-[0_0_12px_rgba(6,182,212,0.15)]'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 rounded-2xl border border-cyan-500/20 bg-[#091b29] flex flex-col justify-between">
              <div>
                <h3 className="text-sm font-bold text-white">Защитившиеся</h3>
                <p className="text-xs text-slate-400 mt-1">
                  {defendedList.length} записей. Дата защиты зафиксирована в едином реестре кафедры.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setDefenseStatus('Защищено');
                  setActiveTab('registry');
                }}
                className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-xl border border-cyan-500/30 bg-[#0c2336] hover:bg-[#102d44] text-cyan-300 text-xs font-semibold transition-all"
              >
                Показать защищённых
              </button>
            </div>

            <div className="p-5 rounded-2xl border border-rose-500/20 bg-[#091b29] flex flex-col justify-between">
              <div>
                <h3 className="text-sm font-bold text-white">Без зафиксированной защиты</h3>
                <p className="text-xs text-slate-400 mt-1">
                  {withoutDefenseList.length} записей. Требуют контроля кафедры и научного руководителя.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setDefenseStatus('Без даты защиты');
                  setActiveTab('registry');
                }}
                className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-xl border border-rose-500/30 bg-[#1a111a] hover:bg-[#251525] text-rose-300 text-xs font-semibold transition-all"
              >
                Показать без даты
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-white">Последние записи выборки</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Нажмите на карточку докторанта для открытия цифрового досье
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('registry')}
                className="px-3.5 py-1.5 rounded-lg border border-cyan-500/30 bg-[#0c2336] text-cyan-300 text-xs font-semibold hover:bg-[#112f48]"
              >
                Полный реестр
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {students.slice(0, 9).map((s: any) => {
                const initials = s.full_name
                  .split(' ')
                  .map((p: string) => p[0])
                  .join('')
                  .slice(0, 2);

                return (
                  <div
                    key={s.id}
                    onClick={() => setSelectedRecordId(s.id)}
                    className="p-3.5 rounded-xl border border-slate-800 bg-[#061420] hover:border-cyan-500/50 hover:bg-[#0b2233] transition-all flex items-center justify-between cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 min-w-0 pr-2">
                      <div className="w-10 h-10 rounded-full bg-cyan-950 border border-cyan-500/30 text-cyan-300 font-bold text-xs flex items-center justify-center shrink-0 group-hover:scale-105 transition">
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs font-bold text-white truncate group-hover:text-cyan-300 transition">{s.full_name}</h4>
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">
                          {s.course} курс · {s.program_code} · {s.supervisor_name} · досье {s.progress}%
                        </p>
                        <p className="text-[10px] text-slate-500 truncate mt-0.5">
                          {s.defense_date ? `Защищён · ${s.defense_date}` : 'Без зафиксированной защиты'}
                        </p>
                      </div>
                    </div>
                    <ChevronRightArrowIcon className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 shrink-0 transition" />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl border border-rose-500/20 bg-[#091b29]/80 p-5">
            <h3 className="text-sm font-bold text-white">Риск-радар НИРД</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Низкий прогресс или незакрытый НИРД — для персонального контроля кафедры
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              {riskStudents.map((s: any) => (
                <div
                  key={s.id}
                  onClick={() => setSelectedRecordId(s.id)}
                  className="p-3 rounded-xl bg-[#061420] border border-rose-900/40 hover:border-rose-500/60 hover:bg-[#1a0f18] transition cursor-pointer"
                >
                  <span className="text-[10px] font-mono text-rose-400 block">{s.student_code}</span>
                  <h5 className="text-xs font-bold text-white mt-1 truncate">{s.full_name}</h5>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {s.course} курс · НИРД: <span className="text-rose-400 font-semibold">{s.nird_status}</span>
                  </p>
                  <p className="text-[11px] text-slate-400">Прогресс: {s.progress}%</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'years' && (
        <div className="space-y-6">
          {cohortStats.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {cohortStats.map((cohort: any) => (
                <div key={cohort.admission_year} className="p-4 rounded-2xl bg-[#091b29] border border-cyan-500/20">
                  <span className="text-[10px] font-bold uppercase text-cyan-400 block">Когорта {cohort.admission_year} года</span>
                  <div className="flex items-baseline justify-between mt-2">
                    <span className="text-2xl font-black text-white">{cohort.total_students}</span>
                    <span className="text-xs text-slate-400">{cohort.course} курс</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-800 text-[11px]">
                    <div>
                      <span className="text-slate-500 block">Защищено</span>
                      <span className="text-emerald-400 font-bold">{cohort.defended_count}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Без защиты</span>
                      <span className="text-rose-400 font-bold">{cohort.without_defense_count}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Риск НИРД</span>
                      <span className="text-amber-400 font-bold">{cohort.nird_risk_count}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Ср. прогресс</span>
                      <span className="text-cyan-400 font-bold">{cohort.avg_progress}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {[1, 2, 3].map((yr) => {
            const list = groupedByYear[yr] || [];
            return (
              <div key={yr} className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-white">{yr} курс</h3>
                  <span className="text-xs text-cyan-400 font-semibold">{list.length} докторантов</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {list.map((s: any) => (
                    <div
                      key={s.id}
                      onClick={() => setSelectedRecordId(s.id)}
                      className="p-3.5 rounded-xl bg-[#061420] border border-slate-800 hover:border-cyan-500/50 hover:bg-[#0b2233] transition cursor-pointer"
                    >
                      <h4 className="text-xs font-bold text-white truncate">{s.full_name}</h4>
                      <p className="text-[11px] text-slate-400 mt-1">{s.program_title} ({s.program_code})</p>
                      <p className="text-[11px] text-slate-400">Руководитель: {s.supervisor_name}</p>
                      <p className="text-[11px] text-cyan-400 mt-1">Прогресс: {s.progress}% · НИРД: {s.nird_status}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'supervisors' && (
        <div className="space-y-6">
          {supervisorStats.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {supervisorStats.map((sup: any) => (
                <div
                  key={sup.supervisor_name}
                  onClick={() => {
                    setSupervisor(sup.supervisor_name);
                    setActiveTab('registry');
                  }}
                  className="p-4 rounded-2xl bg-[#091b29] border border-cyan-500/20 hover:border-cyan-500/50 cursor-pointer transition"
                >
                  <span className="text-[10px] font-bold uppercase text-cyan-400 block">Научный руководитель</span>
                  <h4 className="text-sm font-bold text-white mt-1">{sup.supervisor_name}</h4>
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-800 text-[11px] text-center">
                    <div>
                      <span className="text-slate-500 block">Всего</span>
                      <span className="text-white font-bold">{sup.total_students}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Защищено</span>
                      <span className="text-emerald-400 font-bold">{sup.defended_count}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Ср. %</span>
                      <span className="text-cyan-400 font-bold">{sup.avg_progress}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {Object.entries(groupedBySupervisor).map(([sup, list]) => (
            <div key={sup} className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-white">{sup}</h3>
                <span className="text-xs text-cyan-400 font-semibold">{list.length} докторантов</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((s: any) => (
                  <div
                    key={s.id}
                    onClick={() => setSelectedRecordId(s.id)}
                    className="p-3.5 rounded-xl bg-[#061420] border border-slate-800 hover:border-cyan-500/50 hover:bg-[#0b2233] transition cursor-pointer"
                  >
                    <h4 className="text-xs font-bold text-white truncate">{s.full_name}</h4>
                    <p className="text-[11px] text-slate-400 mt-1">{s.course} курс · {s.program_code}</p>
                    <p className="text-[11px] text-cyan-400 mt-1">Прогресс: {s.progress}% · Защита: {s.defense_date || '—'}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'registry' && (
        <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-800 bg-[#061420] text-slate-400 uppercase text-[10px] font-bold">
                  <th className="py-3 px-3 w-10 text-center">#</th>
                  <th className="py-3 px-4">Докторант</th>
                  <th className="py-3 px-3 text-center">Курс</th>
                  <th className="py-3 px-4">Программа</th>
                  <th className="py-3 px-4">Место работы</th>
                  <th className="py-3 px-3 text-center">НИРД</th>
                  <th className="py-3 px-4">Прогресс</th>
                  <th className="py-3 px-4">Научный руководитель</th>
                  <th className="py-3 px-4 text-center">Защита</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {students.map((s: any, idx: number) => (
                  <tr
                    key={s.id}
                    onClick={() => setSelectedRecordId(s.id)}
                    className="hover:bg-[#0c2336] transition-colors cursor-pointer group"
                  >
                    <td className="py-3 px-3 text-center font-mono text-slate-500">{idx + 1}</td>
                    <td className="py-3 px-4">
                      <div className="font-bold text-white group-hover:text-cyan-300 transition">{s.full_name}</div>
                      <div className="text-[10px] font-mono text-slate-400">
                        {s.student_code} · {s.age} лет
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center font-bold text-cyan-400">{s.course}</td>
                    <td className="py-3 px-4 text-slate-300">
                      <div className="font-medium">{s.program_code}</div>
                      <div className="text-[10px] text-slate-500">{s.program_title}</div>
                    </td>
                    <td className="py-3 px-4 text-slate-300">{s.workplace}</td>
                    <td className="py-3 px-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                        s.nird_status === 'Сдан'
                          ? 'bg-emerald-950 text-emerald-300 border border-emerald-800'
                          : s.nird_status === 'На проверке'
                          ? 'bg-cyan-950 text-cyan-300 border border-cyan-800'
                          : s.nird_status === 'Черновик'
                          ? 'bg-amber-950 text-amber-300 border border-amber-800'
                          : 'bg-rose-950 text-rose-300 border border-rose-800'
                      }`}>
                        {s.nird_status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-bold font-mono text-white text-[11px] w-8">
                          {s.progress}%
                        </span>
                        <div className="w-24 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                          <div
                            className="h-full bg-cyan-400 rounded-full"
                            style={{ width: `${s.progress}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-300 font-medium">{s.supervisor_name}</td>
                    <td className="py-3 px-4 text-center">
                      {s.defense_date ? (
                        <div className="inline-block px-2.5 py-1 rounded-lg bg-emerald-950/80 border border-emerald-600/40 text-emerald-300 text-left">
                          <div className="font-bold text-[10px]">Защищён</div>
                          <div className="text-[9px] font-mono text-emerald-400">{s.defense_date}</div>
                        </div>
                      ) : (
                        <div className="inline-block px-2 py-1 rounded-lg bg-slate-800/60 border border-slate-700/60 text-slate-400 text-[10px]">
                          <div>Без зафиксированной защиты</div>
                          <div className="text-center font-mono">—</div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showEnrollModal && (
        <div className="cab-overlay">
          <div className="cab-shell cab-shell--wide cab-shell--tall bg-[#091b29] border border-cyan-500/30 rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-slate-800 bg-[#061420] flex items-center justify-between">
              <h3 className="text-base font-bold text-white">Зачисление докторанта в контингент кафедры</h3>
              <button
                onClick={() => setShowEnrollModal(false)}
                className="w-8 h-8 rounded-lg bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleEnrollSubmit} className="p-6 space-y-4">
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                  ФИО докторанта *
                </label>
                <input
                  type="text"
                  required
                  value={enrollForm.full_name}
                  onChange={(e) => setEnrollForm({ ...enrollForm, full_name: e.target.value })}
                  placeholder="Фамилия Имя Отчество"
                  className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                    Шифр / Студенческий ID
                  </label>
                  <input
                    type="text"
                    value={enrollForm.student_code}
                    onChange={(e) => setEnrollForm({ ...enrollForm, student_code: e.target.value })}
                    placeholder="D017-150"
                    className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                    Курс
                  </label>
                  <select
                    value={enrollForm.course}
                    onChange={(e) => setEnrollForm({ ...enrollForm, course: Number(e.target.value) })}
                    className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  >
                    <option value={1}>1 курс</option>
                    <option value={2}>2 курс</option>
                    <option value={3}>3 курс</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                    Образовательная программа
                  </label>
                  <select
                    value={enrollForm.program_code}
                    onChange={(e) => {
                      const code = e.target.value;
                      const title = code === '8D01717'
                        ? 'Казахский язык и литература'
                        : code === '8D02305'
                        ? 'Тюркология'
                        : 'Филология';
                      setEnrollForm({ ...enrollForm, program_code: code, program_title: title });
                    }}
                    className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  >
                    <option value="8D01717">8D01717 - Казахский язык и литература</option>
                    <option value="8D02305">8D02305 - Тюркология</option>
                    <option value="8D02301">8D02301 - Филология</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                    Научный руководитель
                  </label>
                  <select
                    value={enrollForm.supervisor_name}
                    onChange={(e) => setEnrollForm({ ...enrollForm, supervisor_name: e.target.value })}
                    className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  >
                    <option value="Syrym Zhanzhigitov">Syrym Zhanzhigitov</option>
                    <option value="А. Қайдарова">А. Қайдарова</option>
                    <option value="Б. Омарова">Б. Омарова</option>
                    <option value="Жусупова А.К.">Жусупова А.К.</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                    Зарубежный консультант
                  </label>
                  <input
                    type="text"
                    value={enrollForm.foreign_consultant_name}
                    onChange={(e) => setEnrollForm({ ...enrollForm, foreign_consultant_name: e.target.value })}
                    placeholder="Dr. Hans Meyer"
                    className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                    Место работы
                  </label>
                  <input
                    type="text"
                    value={enrollForm.workplace}
                    onChange={(e) => setEnrollForm({ ...enrollForm, workplace: e.target.value })}
                    placeholder="ЕНУ им. Л.Н. Гумилева"
                    className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>
              </div>

              <div className="pt-3 flex justify-end gap-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowEnrollModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={isEnrolling}
                  className="px-5 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs transition shadow disabled:opacity-50"
                >
                  {isEnrolling ? 'Зачисление...' : 'Зачислить докторанта'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedRecordId && (
        <DoctoralDossierModal
          recordId={selectedRecordId}
          onClose={() => setSelectedRecordId(null)}
          onRecordUpdated={fetchData}
        />
      )}
    </div>
  );
};
